import { useState, useEffect } from "react";
import { MdAdd } from "react-icons/md";
import { useProjects } from "./Hooks/useProjects";
import {
  GenericTable,
  Pagination,
  RoleValidator,
  FilterCard,
} from "../../Components/Common";
import {
  NO_PROJECTS_FOUND,
  PROJECT_LIST_PAGE_COLUMNS,
  PROJECT_LIST_PAGE_LABELS,
  PROJECT_STATUS,
  PROJECT_TYPES,
  CATEGORY_TYPES,
  LABEL_MAPPINGS,
  STATUS_STYLES,
  PROJECT_STATUS_LABELS,
  PROJECT_FILTER_TYPES,
} from "./constant";
import { useNavigate } from "react-router-dom";
import { CREATE_PROJECT, VIEW_PROJECT } from "../../routes";
import { ROLES } from "../../Constants/Strings";
import { getLabel } from "../../Utils";

const ProjectListPage = () => {
  const {
    projects,
    loading,
    filters,
    error,
    setFilters,
    page,
    onPageChange,
    onPageSizeChange,
    totalPages,
    rowsPerPage,
    fetchProjects,
  } = useProjects();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  

  useEffect(() => {
    fetchProjects();
  }, [page, rowsPerPage, filters]);

  // Mobile card rendering
  const renderMobileCard = (row) => (
    <div
      className="px-4"
      key={row.id}
      onClick={() => navigate(VIEW_PROJECT.replace(":instanceId", row.id))}
    >
      <div className="w-full grid grid-cols-2 bg-white hover:shadow-md transition border-b border-gray-400 py-3">
        <div className="flex flex-col justify-between items-start w-[300px]">
          <h3
            className="text-base font-semibold text-gray-900 truncate max-w-[50%]"
            title={row.name}
          >
            {row.name || "Untitled Project"}
          </h3>
          <div className="flex gap-2 flex-wrap text-sm">
            <p className="truncate" title={row.type}>
              {getLabel(LABEL_MAPPINGS.type, row.type) || "-"}
            </p>
            |
            <p className="truncate" title={row.category}>
              {getLabel(LABEL_MAPPINGS.category, row.category) || "-"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-10 justify-end">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
              STATUS_STYLES[row.status] || STATUS_STYLES.default
            }`}
          >
            {PROJECT_STATUS_LABELS[row.status] || "-"}
          </span>
          <div className="flex gap-2 flex-wrap">
            <p>{row.completion_percentage}%</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Table cell rendering
  const actionsRenderField = (key, row) => {
    if (key === "completion_percentage") {
      return <div className="w-1/2 flex justify-center">{row[key]}%</div>;
    }

    if (key === "status") {
      return (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
            STATUS_STYLES[row.status] || STATUS_STYLES.default
          }`}
        >
          {PROJECT_STATUS_LABELS[row.status] || "-"}
        </span>
      );
    }

    if (key === "client_name") {
      return (
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px] overflow-hidden">
          {row?.client_details?.name || "-"}
        </span>
      );
    }

    if (LABEL_MAPPINGS[key]) {
      return getLabel(LABEL_MAPPINGS[key], row[key]);
    }

    return row[key];
  };

  return (
    <div>
      <div className="grid grid-cols-12 items-center gap-4 mb-2 px-3 py-2 mt-3">
        <div className="col-span-12 md:col-span-10 flex items-center justify-between pl-2">
          <h1 className="text-2xl font-bold">Projects</h1>

          {/* Mobile: full button*/}
          <div className="md:hidden mt-4">
            <RoleValidator
              allowedRoles={[
                ROLES.MANAGING_DIRECTOR,
                ROLES.HEAD_OFFICE,
                ROLES.GOVERNMENT_OFFICER,
              ]}
              mode="disable"
            >
              <button
                onClick={() => navigate(CREATE_PROJECT)}
                className="bg-[#a2d2ff] text-white px-2 py-1 rounded-md hover:bg-[#bde0fe] max-h-fit"
                aria-label="Add project"
              >
                <MdAdd size={28} />
              </button>
            </RoleValidator>
          </div>
        </div>

        {/* Desktop Add Project button (hidden on mobile, sits at far right on md+) */}
        <div className="hidden md:flex md:col-span-1 md:ml-4">
          <RoleValidator
            allowedRoles={[
              ROLES.MANAGING_DIRECTOR,
              ROLES.HEAD_OFFICE,
              ROLES.GOVERNMENT_OFFICER,
            ]}
            mode="disable"
          >
            <button
              onClick={() => navigate(CREATE_PROJECT)}
              className="bg-primary text-white px-3 py-2 rounded-md flex items-center gap-2"
              aria-label="Add project"
            >
              <MdAdd size={18} />
              <span className="whitespace-nowrap">Add Project</span>
            </button>
          </RoleValidator>
        </div>
      </div>

      <div className="mb-4 px-3">
        <div>
          <FilterCard
            tempFilters={filters}
            setTempFilters={(updatedFilters) => {
              setFilters(updatedFilters);
              onPageChange(1);
            }}
            searchText={searchText}
            setSearchText={setSearchText}
            fields={[
              {
                key: "search",
                label: "Search",
                type: "search",
                placeholder: "Search by project name",
              },
              {
                key: "project",
                label: "Projects",
                type: "select",
                options: PROJECT_FILTER_TYPES,
              },
              {
                key: "type",
                label: "Type",
                type: "select",
                options: [{ value: "", label: "All" }, ...PROJECT_TYPES],
              },
              {
                key: "category",
                label: "Category",
                type: "select",
                options: [{ value: "", label: "All" }, ...CATEGORY_TYPES],
              },
              {
                key: "status",
                label: "Status",
                type: "select",
                options: [{ value: "", label: "All" }, ...PROJECT_STATUS],
              },
            ]}
          />
        </div>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {/* Projects Table */}
      <div className="flex flex-col md:flex-row gap-6">
        {loading ? (
          <div className="flex justify-center items-center w-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="w-full flex flex-col">
            <div className="w-full flex flex-col h-[61vh] md:h-[53vh] overflow-x-auto">
              <GenericTable
                tableColumns={PROJECT_LIST_PAGE_COLUMNS}
                columnLabels={PROJECT_LIST_PAGE_LABELS}
                tableRows={projects}
                error={NO_PROJECTS_FOUND}
                mobileGridColumns={4}
                enableGridLayout={true}
                renderMobileCard={renderMobileCard}
                renderField={actionsRenderField}
                rowClick={(row) =>
                  navigate(VIEW_PROJECT.replace(":instanceId", row.id))
                }
              />
            </div>

            <div className="shrink-0">
              <Pagination
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                totalPages={totalPages}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectListPage;
